/**
 * Typed HTTP error class for consistent error handling
 */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = "HttpError";
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HttpError);
    }
  }
}

/**
 * Check if error is an HttpError
 */
export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export class RequestTimeoutError extends HttpError {
  constructor() {
    super("Request timed out. Check the operation status before retrying.", 408);
    this.name = "RequestTimeoutError";
  }
}

type Primitive = string | number | boolean | null | undefined;
type QueryValue = Primitive | Primitive[];

type FetcherParams = Record<string, QueryValue>;

type FetcherRequest = {
  url: string;
  method?: string;
  headers?: HeadersInit;
  data?: unknown;
  signal?: AbortSignal;
  params?: FetcherParams;
};

export type AuthSessionId = string | number;

export type AuthSnapshot = {
  sessionId: AuthSessionId | undefined;
  token: string | undefined;
};

export type FetcherConfig = RequestInit & {
  baseUrl?: string;
  /** Deadline for the complete request, including authentication and body reads. */
  timeoutMs?: number;
  getToken?: () => string | undefined | Promise<string | undefined>;
  /** Returns a token and its owning session as one consistent snapshot. */
  getAuthSnapshot?: () => AuthSnapshot | Promise<AuthSnapshot>;
  /** Identifies the auth session that owns a request and its possible retry. */
  getAuthSessionId?: () =>
    | AuthSessionId
    | undefined
    | Promise<AuthSessionId | undefined>;
  /** Synchronous final guard used immediately before dispatching a request. */
  isAuthSessionCurrent?: (
    expectedSessionId: AuthSessionId | undefined,
  ) => boolean;
  /** Clears tokens only if the expected session still owns them. */
  clearAuthSession?: (
    expectedSessionId: AuthSessionId | undefined,
  ) => Promise<boolean>;
  /**
   * Optional function to get wallet ID for X-Wallet-Id header.
   * Should return wallet ID string or undefined if wallet is not available.
   */
  getWalletId?: () => string | undefined | Promise<string | undefined>;
  /**
   * Optional function to refresh the access token when a 401 response is received.
   * Should return a promise that resolves when the token has been refreshed.
   * If this function is not provided or returns false/rejects, the original 401 error will be thrown.
   * @returns Promise<boolean> - true if token was refreshed successfully, false otherwise
   */
  onRefresh?: (expectedSessionId?: AuthSessionId) => Promise<boolean>;
};

const isFetcherRequest = (value: unknown): value is FetcherRequest =>
  typeof value === "object" && value !== null && "url" in value;

const throwIfAborted = (signal: AbortSignal): void => {
  if (signal.aborted) {
    throw signal.reason ?? new DOMException("The request was aborted", "AbortError");
  }
};

const isAbsoluteUrl = (value: string): boolean =>
  /^([a-z][a-z\d+\-.]*:)?\/\//i.test(value);

const combineUrl = (base: string | undefined, path: string): string => {
  if (!base || isAbsoluteUrl(path)) return path;
  // If the path already starts with the base, don't add it again
  if (path.startsWith(base)) return path;
  if (base.endsWith("/") && path.startsWith("/")) return base + path.slice(1);
  if (!base.endsWith("/") && !path.startsWith("/")) return `${base}/${path}`;
  return `${base}${path}`;
};

const appendParams = (url: string, params?: FetcherParams): string => {
  if (!params || Object.keys(params).length === 0) return url;

  const parts = url.split("?");
  const basePart = parts[0] ?? url; // split always returns at least one element
  const existingQuery = parts[1];
  const searchParams = new URLSearchParams(existingQuery ?? "");

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${basePart}?${queryString}` : basePart;
};

const hasHeader = (headers: HeadersInit | undefined, name: string): boolean => {
  if (!headers) return false;
  if (headers instanceof Headers) {
    return headers.has(name);
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => key.toLowerCase() === name.toLowerCase());
  }
  return (
    name.toLowerCase() in
    Object.keys(headers).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    )
  );
};

const mergeHeaders = (...sources: (HeadersInit | undefined)[]): Headers => {
  const result = new Headers();

  for (const source of sources) {
    if (!source) continue;

    if (source instanceof Headers) {
      source.forEach((value, key) => result.set(key, value));
      continue;
    }

    if (Array.isArray(source)) {
      source.forEach(([key, value]) => result.set(key, value));
      continue;
    }

    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined) {
        result.set(key, value);
      }
    });
  }

  return result;
};

const stripFetcherConfig = (cfg?: FetcherConfig): RequestInit | undefined => {
  if (!cfg) return undefined;
  const {
    baseUrl: _baseUrl,
    timeoutMs: _timeoutMs,
    getToken: _getToken,
    getAuthSnapshot: _getAuthSnapshot,
    getAuthSessionId: _getAuthSessionId,
    isAuthSessionCurrent: _isAuthSessionCurrent,
    clearAuthSession: _clearAuthSession,
    getWalletId: _getWalletId,
    onRefresh: _onRefresh,
    headers: _headers,
    ...rest
  } = cfg;
  return rest;
};

const prepareBody = (
  data: unknown,
): { body?: BodyInit; headers?: HeadersInit } => {
  if (data === undefined || data === null) {
    return {};
  }

  if (
    typeof data === "string" ||
    data instanceof Blob ||
    data instanceof FormData ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  ) {
    return { body: data as BodyInit };
  }

  return {
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  };
};

const performRequest = async <T>(
  request: FetcherRequest | string,
  cfg: FetcherConfig | undefined,
  requestSignal: AbortSignal,
): Promise<T> => {
  throwIfAborted(requestSignal);
  const overrideInit = stripFetcherConfig(cfg) ?? {};
  const readAuthSnapshot = async (): Promise<AuthSnapshot> => {
    if (cfg?.getAuthSnapshot) {
      return Promise.resolve(cfg.getAuthSnapshot());
    }

    // Legacy fallback. Auth-sensitive callers should provide getAuthSnapshot so
    // the token and its owner are captured atomically.
    const authSessionIdResult = cfg?.getAuthSessionId?.();
    const tokenResult = cfg?.getToken?.();
    const [sessionId, snapshotToken] = await Promise.all([
      Promise.resolve(authSessionIdResult),
      Promise.resolve(tokenResult),
    ]);
    return { sessionId, token: snapshotToken };
  };

  const authSnapshot = await readAuthSnapshot();
  const token = authSnapshot.token;
  const walletId = await Promise.resolve(cfg?.getWalletId?.());
  const authSessionIsCurrent = (): boolean =>
    !cfg?.isAuthSessionCurrent ||
    cfg.isAuthSessionCurrent(authSnapshot.sessionId);

  let urlPath: string;
  let method: string | undefined;
  let requestHeaders: HeadersInit | undefined;
  let params: FetcherParams | undefined;
  let body: BodyInit | undefined;
  let bodyHeaders: HeadersInit | undefined;

  if (isFetcherRequest(request)) {
    urlPath = request.url;
    method = request.method;
    requestHeaders = request.headers;
    params = request.params;

    const prepared = prepareBody(request.data);
    body = prepared.body;
    bodyHeaders = prepared.headers;
  } else {
    urlPath = request;
  }

  const fallbackBaseUrl =
    (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? "/api";
  const urlWithBase = combineUrl(cfg?.baseUrl ?? fallbackBaseUrl, urlPath);
  const finalUrl = appendParams(urlWithBase, params);

  const headers = mergeHeaders(
    bodyHeaders,
    requestHeaders,
    cfg?.headers,
    // Only add Authorization header if X-Auth-Bearer is not already present (for Telecom API compatibility)
    token &&
      !hasHeader(requestHeaders, "X-Auth-Bearer") &&
      !hasHeader(cfg?.headers, "X-Auth-Bearer")
      ? { Authorization: `Bearer ${token}` }
      : undefined,
    // Add X-Wallet-Id header if walletId is available and not already present
    walletId &&
      !hasHeader(requestHeaders, "X-Wallet-Id") &&
      !hasHeader(cfg?.headers, "X-Wallet-Id")
      ? { "X-Wallet-Id": walletId }
      : undefined,
  );

  const responseInit: RequestInit = {
    ...overrideInit,
    method: method ?? overrideInit.method,
    body: body ?? overrideInit.body,
    signal: requestSignal,
    headers,
  };

  if (!authSessionIsCurrent()) {
    throw new HttpError("Authentication session changed before request", 401);
  }
  throwIfAborted(requestSignal);
  const initialRequest = fetch(finalUrl, responseInit);
  let res = await initialRequest;
  throwIfAborted(requestSignal);

  // Handle 401 Unauthorized with token refresh
  // Only attempt refresh once to prevent infinite retry loops
  if (res.status === 401 && cfg?.onRefresh) {
    if (!authSessionIsCurrent()) {
      // Preserve and parse the original 401 below.
    } else {
      try {
        const currentSnapshot = await readAuthSnapshot();
        const sessionStillMatches =
          currentSnapshot.sessionId === authSnapshot.sessionId &&
          authSessionIsCurrent();

        if (sessionStillMatches) {
          const tokenAlreadyRefreshed = Boolean(
            currentSnapshot.token && currentSnapshot.token !== token,
          );
          const refreshed =
            tokenAlreadyRefreshed ||
            (await cfg.onRefresh(authSnapshot.sessionId));

          if (refreshed && authSessionIsCurrent()) {
            const retrySnapshot = await readAuthSnapshot();
            const retryWalletId = await Promise.resolve(cfg.getWalletId?.());
            const retryHeaders = mergeHeaders(
              bodyHeaders,
              requestHeaders,
              cfg.headers,
              retrySnapshot.token &&
                !hasHeader(requestHeaders, "X-Auth-Bearer") &&
                !hasHeader(cfg.headers, "X-Auth-Bearer")
                ? { Authorization: `Bearer ${retrySnapshot.token}` }
                : undefined,
              retryWalletId &&
                !hasHeader(requestHeaders, "X-Wallet-Id") &&
                !hasHeader(cfg.headers, "X-Wallet-Id")
                ? { "X-Wallet-Id": retryWalletId }
                : undefined,
            );
            const retryResponseInit: RequestInit = {
              ...overrideInit,
              method: method ?? overrideInit.method,
              body: body ?? overrideInit.body,
              signal: requestSignal,
              headers: retryHeaders,
            };

            if (
              retrySnapshot.sessionId === authSnapshot.sessionId &&
              authSessionIsCurrent()
            ) {
              // No await is allowed between this synchronous generation guard
              // and dispatching the retry.
              throwIfAborted(requestSignal);
              const retryRequest = fetch(finalUrl, retryResponseInit);
              res = await retryRequest;

              if (res.status === 401 && authSessionIsCurrent()) {
                await cfg.clearAuthSession?.(authSnapshot.sessionId);
                throw new HttpError(
                  `HTTP ${res.status}: Authentication failed after token refresh. Tokens may not match current (telegramId, walletId) pair.`,
                  res.status,
                );
              }
            }
          } else if (authSessionIsCurrent()) {
            throw new HttpError(
              `HTTP ${res.status}: Token refresh failed`,
              res.status,
            );
          }
        }
      } catch (refreshError) {
        throwIfAborted(requestSignal);
        // If refresh fails or retry returns 401, throw the error
        // Don't wrap HttpError in another HttpError
        if (refreshError instanceof HttpError) {
          throw refreshError;
        }
        // If refresh fails, throw the original 401 error
        throw new HttpError(`HTTP ${res.status}`, res.status);
      }
    }
  }

  if (!res.ok) {
    // Parse error response if available
    const contentType = res.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await res.json();
        throw new HttpError(
          (errorData as { message?: string })?.message ||
            `HTTP ${res.status}: ${res.statusText}`,
          res.status,
          errorData,
        );
      } catch (parseError) {
        // If JSON parsing fails, throw generic error
        if (parseError instanceof HttpError) {
          throw parseError;
        }
        throw new HttpError(
          `HTTP ${res.status}: ${res.statusText}`,
          res.status,
        );
      }
    }
    throw new HttpError(`HTTP ${res.status}: ${res.statusText}`, res.status);
  }

  const text = await res.text();
  if (!text) {
    // Return undefined for empty responses, but typed correctly
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    // If JSON parsing fails, return text as-is (for text responses)
    return text as T;
  }
};

export const customFetcher = async <T>(
  request: FetcherRequest | string,
  cfg?: FetcherConfig,
): Promise<T> => {
  const callerSignal = isFetcherRequest(request)
    ? request.signal ?? cfg?.signal
    : cfg?.signal;
  const controller = new AbortController();
  const configuredTimeout = cfg?.timeoutMs;
  const timeoutMs = typeof configuredTimeout === "number" &&
    Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : DEFAULT_REQUEST_TIMEOUT_MS;
  const cancelFromCaller = () => controller.abort(callerSignal?.reason);
  const cancelled = new Promise<never>((_, reject) => {
    controller.signal.addEventListener("abort", () => {
      reject(controller.signal.reason ?? new DOMException("The request was aborted", "AbortError"));
    }, { once: true });
  });
  callerSignal?.addEventListener("abort", cancelFromCaller, { once: true });
  if (callerSignal?.aborted) cancelFromCaller();
  const deadline = setTimeout(() => controller.abort(new RequestTimeoutError()), timeoutMs);

  try {
    // The race also bounds native/auth callbacks that do not support AbortSignal.
    return await Promise.race([
      performRequest<T>(request, cfg, controller.signal),
      cancelled,
    ]);
  } finally {
    clearTimeout(deadline);
    callerSignal?.removeEventListener("abort", cancelFromCaller);
  }
};

export default customFetcher;
